import React, { useRef, useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const MobileSlider = ({ children, showDots = true, showArrows = true, className = '', darkMode = false }) => {
  const scrollRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [totalSlides, setTotalSlides] = useState(0);

  useEffect(() => {
    if (scrollRef.current) {
      setTotalSlides(scrollRef.current.children.length);
    }
  }, [children]);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const handleScroll = () => {
      const scrollLeft = container.scrollLeft;
      const slideWidth = container.offsetWidth;
      const index = Math.round(scrollLeft / slideWidth);
      setActiveIndex(index);
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (index) => {
    if (scrollRef.current) {
      const slideWidth = scrollRef.current.offsetWidth;
      scrollRef.current.scrollTo({ left: slideWidth * index, behavior: 'smooth' });
    }
  };

  const handlePrev = () => {
    if (activeIndex > 0) scrollTo(activeIndex - 1);
  };

  const handleNext = () => {
    if (activeIndex < totalSlides - 1) scrollTo(activeIndex + 1);
  };

  const arrowBase = darkMode
    ? 'border-white/20 text-white/50 hover:text-white hover:border-white/40 disabled:opacity-20'
    : 'border-[#E5E7EB] text-[#9CA3AF] hover:text-[#111827] hover:border-[#111827] disabled:opacity-20';

  const dotActive = darkMode ? 'bg-[#FF1010]' : 'bg-[#FF1010]';
  const dotInactive = darkMode ? 'bg-white/20 hover:bg-white/40' : 'bg-[#E5E7EB] hover:bg-[#9CA3AF]';

  return (
    <div className={`relative ${className}`} data-testid="mobile-slider">
      <div
        ref={scrollRef}
        className="flex overflow-x-auto snap-x snap-mandatory gap-0"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
      >
        {React.Children.map(children, (child, i) => (
          <div key={i} className="w-full flex-shrink-0 snap-center px-3">
            {child}
          </div>
        ))}
      </div>

      {totalSlides > 1 && (
        <div className="flex items-center justify-center gap-4 mt-5">
          {showArrows && (
            <button
              onClick={handlePrev}
              disabled={activeIndex === 0}
              data-testid="slider-prev"
              className={`w-8 h-8 border flex items-center justify-center transition-all ${arrowBase}`}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
          )}

          {showDots && (
            <div className="flex items-center gap-1.5">
              {Array.from({ length: totalSlides }).map((_, i) => (
                <button
                  key={i}
                  onClick={() => scrollTo(i)}
                  data-testid={`slider-dot-${i}`}
                  className={`transition-all duration-200 ${
                    i === activeIndex ? `w-6 h-1.5 ${dotActive}` : `w-1.5 h-1.5 ${dotInactive}`
                  }`}
                />
              ))}
            </div>
          )}

          {showArrows && (
            <button
              onClick={handleNext}
              disabled={activeIndex === totalSlides - 1}
              data-testid="slider-next"
              className={`w-8 h-8 border flex items-center justify-center transition-all ${arrowBase}`}
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default MobileSlider;
